import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_FULL, LOGO_SHIELD } from "@/lib/api";
import { useBranding } from "@/hooks/useBranding";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, CalendarDays, Users, Briefcase, HardHat,
  BarChart3, Settings, LogOut, Menu, X, ClipboardList, ClipboardCheck, FileText, Receipt, ListTree, CalendarClock, Link2, Wallet, Package, TrendingUp, MailCheck,
} from "lucide-react";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/job-confirmations", label: "Job Confirmations", icon: MailCheck },
  { to: "/quotes", label: "Quotes", icon: FileText },
  { to: "/waiting-to-invoice", label: "Waiting to Invoice", icon: ClipboardCheck },
  { to: "/weekly-billing", label: "Ready to Bill", icon: Receipt },
  { to: "/invoices-awaiting-payment", label: "Awaiting Payment", icon: Wallet },
  { to: "/price-list", label: "Price List", icon: ListTree },
  { to: "/materials", label: "Materials", icon: Package },
  { to: "/holidays-rdo", label: "Holidays & RDOs", icon: CalendarClock },
  { to: "/employees", label: "Employees", icon: HardHat },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/reports/cashflow", label: "Cashflow", icon: TrendingUp },
  { to: "/timesheets", label: "Timesheets", icon: ClipboardList },
  { to: "/leave", label: "Leave & Availability", icon: CalendarClock },
];

const techNav = [
  { to: "/my-jobs", label: "My Jobs", icon: ClipboardList },
  { to: "/timesheets", label: "Timesheets", icon: FileText },
  { to: "/leave", label: "My Leave", icon: CalendarClock },
];

const adminBottomNav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/timesheets", label: "Timesheets", icon: ClipboardList },
];

export function StatusPill({ status, invoiceStatus, invoiceId }) {
  const map = {
    scheduled: "bg-blue-100 text-blue-700",
    in_progress: "bg-emerald-100 text-emerald-700",
    awaiting_signature: "bg-yellow-100 text-yellow-800",
    return_tomorrow: "bg-orange-100 text-orange-700",
    awaiting_return_visit: "bg-purple-100 text-purple-700",
    completed: "bg-slate-200 text-slate-700",
  };
  const label = { scheduled: "Scheduled", in_progress: "In Progress", awaiting_signature: "Awaiting Signature",
    return_tomorrow: "Return Tomorrow", awaiting_return_visit: "Awaiting Return Visit", completed: "Completed" };
  // Billing lifecycle overrides a stale job status: multi-day jobs stay status="scheduled" even
  // after their dockets are queued/invoiced, so reflect the invoice stage instead.
  const billing = {
    waiting_to_invoice: { c: "bg-amber-100 text-amber-800", l: "Waiting to Invoice" },
    ready_for_weekly_invoice: { c: "bg-indigo-100 text-indigo-700", l: "Ready to Bill" },
    included_in_invoice: { c: "bg-teal-100 text-teal-700", l: "Invoiced" },
  };
  let eff = invoiceStatus;
  if (invoiceId && !billing[eff]) eff = "included_in_invoice";
  const activeStatus = status === "in_progress" || status === "awaiting_signature";
  if (billing[eff] && !activeStatus) {
    const b = billing[eff];
    return (
      <span data-testid={`status-pill-${eff}`} className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${b.c}`}>
        {b.l}
      </span>
    );
  }
  return (
    <span data-testid={`status-pill-${status}`} className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${map[status] || map.scheduled}`}>
      {label[status] || status}
    </span>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? adminNav : techNav;
  const { branding, asset } = useBranding();
  const fullLogo = branding?.logo_full_url ? asset(branding.logo_full_url) : LOGO_FULL;
  const shieldLogo = branding?.logo_shield_url ? asset(branding.logo_shield_url) : LOGO_SHIELD;
  const bizName = branding?.trading_name || "True Seal Caulking";

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-navy text-white fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10 shrink-0">
          <img src={fullLogo} alt={bizName} className="h-16 object-contain" />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {nav.map((item, i) => (
            item.divider ? (
              <div key={`div-${i}`} className="my-2 border-t border-white/10" />
            ) : (
            <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
              <item.icon className="w-5 h-5" /> {item.label}
            </NavLink>
            )
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 shrink-0">
          {user?.role === "admin" && (
            <NavLink to="/settings" data-testid="nav-settings-pinned"
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 mb-1 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
              <Settings className="w-5 h-5" /> Settings
            </NavLink>
          )}
          <div className="px-3 py-2 mb-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10 transition-colors">
            <LogOut className="w-5 h-5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-64 flex flex-col min-h-screen w-full lg:w-[calc(100%-16rem)]">
        {/* Mobile top header */}
        <header className="lg:hidden sticky top-0 z-40 bg-navy text-white flex items-center justify-between px-4 py-3">
          <img src={shieldLogo} alt="logo" className="h-9 w-9 object-contain" />
          <span className="font-heading font-semibold tracking-wide text-sm">TRUE SEAL CAULKING</span>
          <div className="flex items-center gap-1">
            <NotificationBell variant="mobile" />
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-1">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </header>

        {/* Desktop top bar */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-8 py-3 bg-white border-b border-slate-200 sticky top-0 z-30 text-slate-600">
          <NotificationBell variant="desktop" />
        </div>

        {/* Mobile menu drawer */}
        {open && (
          <>
            <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />
            <div className="lg:hidden fixed top-0 right-0 z-50 h-full w-72 max-w-[85%] bg-navy text-white overflow-y-auto shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
                <span className="font-heading font-semibold tracking-wide text-sm">Menu</span>
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-1"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 px-3 py-3 space-y-1">
                {nav.filter((item) => !item.divider).map((item) => (
                  <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10"}`}>
                    <item.icon className="w-5 h-5" /> {item.label}
                  </NavLink>
                ))}
                {user?.role === "admin" && (
                  <NavLink to="/settings" onClick={() => setOpen(false)} data-testid="nav-settings-mobile"
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10"}`}>
                    <Settings className="w-5 h-5" /> Settings
                  </NavLink>
                )}
              </div>
              <div className="p-3 border-t border-white/10">
                <p className="px-4 text-sm font-semibold truncate">{user?.name}</p>
                <p className="px-4 text-xs text-slate-400 capitalize mb-1">{user?.role}</p>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10">
                  <LogOut className="w-5 h-5" /> Sign Out
                </button>
              </div>
            </div>
          </>
        )}

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex justify-around pb-safe">
          {(user?.role === "admin" ? adminBottomNav : techNav).map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`bottomnav-${item.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2 px-3 text-[11px] font-medium ${isActive ? "text-navy" : "text-slate-400"}`}>
              <item.icon className="w-5 h-5" /> {item.label}
            </NavLink>
          ))}
          <button data-testid="bottomnav-more" onClick={() => setOpen(true)} className="flex flex-col items-center gap-1 py-2 px-3 text-[11px] font-medium text-slate-400">
            <Menu className="w-5 h-5" /> More
          </button>
        </nav>
      </div>
    </div>
  );
}
